import * as React from "react";

import PropTypes from "prop-types";
import { NativeSelect, Select } from "@lululemon/ecom-pattern-library";
import cs from "classnames";
import styles from "./quiz-question-dropdown.module.scss";

const QuizDropDownGroup = ({
  questionFieldsData,
  onSaveAnswers,
  tabIndex,
  isEmbedded,
  missingFields,
  fieldValidationError,
}) => {
  const [brand, setBrand] = React.useState();
  const [selectKey, setSelectKey] = React.useState(`select_${Date.now()}`);
  const getAnalyticsValue = (fieldId, selectedValue) => {
    const currentField = ({ id }) => id === fieldId;
    const matchedValueData = ({ value }) => value === selectedValue;

    const selectData = questionFieldsData.find(currentField);

    if (fieldId === "cupSizes") {
      return selectData.brands?.[brand]?.find(matchedValueData)?.analyticsValue;
    }

    return selectData.options.find(matchedValueData)?.analyticsValue;
  };

  const resetCupSizeSelectState = async () => {
    await onSaveAnswers(null, "cupSizes", null);
    // This is a hack that let us reset cup size select.
    // PL Select doesn't expose a way to reset/clear its inner state.
    setSelectKey(`select_${Date.now()}`);
  };

  const onSelect = async (selectedOption, fieldId) => {
    const value = selectedOption?.target.value || null;
    const analyticsValue = getAnalyticsValue(fieldId, value) || null;

    if (fieldId === "brands" && brand !== value) {
      // cup size options change if the brand changes, so force guest to re-select
      await resetCupSizeSelectState();
      setBrand(value);
      await onSaveAnswers(value, fieldId, analyticsValue);
    } else {
      await onSaveAnswers(value, fieldId, analyticsValue);
    }
  };

  const getHint = (showError, isCupSizesSelect, id) => {
    if (!brand && isCupSizesSelect && !showError) {
      const cupSizeSelect = questionFieldsData.find(
        (question) => question.id === "cupSizes"
      );
      return cupSizeSelect?.noPrevAnswerHint;
    }

    if (showError) {
      return missingFields[id].validationErrorData.message;
    }
  };

  return (
    <div
      className={cs({
        [styles.quizQuestionDropdown]: !isEmbedded,
        [styles.quizQuestionDropdownEmbedded]: isEmbedded,
      })}
    >
      {questionFieldsData.map(
        ({ options, label, placeholder, brands, id }, index) => {
          const isCupSizesSelect = id === "cupSizes";
          let selectOptions = options || [];

          const showError = fieldValidationError && id in missingFields;
          const testId = `select-${label
            ?.trim()
            .toLowerCase()
            .replace(/\s/g, "-")}`;

          // Cup Size options are only rendered once a brand is selected
          if (isCupSizesSelect && brand) {
            selectOptions = brands[brand];
          }
          if (isCupSizesSelect && !brand && !isEmbedded) {
            selectOptions = [{ value: "", label: placeholder }];
          }

          const selectProps = {
            required: true,
            status: showError ? "error" : "",
            hint: getHint(showError, isCupSizesSelect, id),
            name: label,
            id: id,
            ...(isEmbedded && { tabIndex }),
            ...(!isEmbedded && { selectProps: { tabIndex } }),
            options: selectOptions,
            placeholder: placeholder,
            "data-testid": testId,
            onChange: (selectedOption) => onSelect(selectedOption, id),
          };

          return (
            <div
              key={id === "cupSizes" ? selectKey : label}
              className={cs({
                [styles.quizQuestionDropdownBox]: !isEmbedded,
                [styles.quizQuestionDropdownBoxEmbedded]: isEmbedded,
              })}
            >
              {!isEmbedded ? (
                <NativeSelect {...selectProps}>{label}</NativeSelect>
              ) : (
                <Select {...selectProps}>{label}</Select>
              )}
            </div>
          );
        }
      )}
    </div>
  );
};

QuizDropDownGroup.defaultProps = {
  isEmbedded: false,
  tabIndex: "-1",
  missingFields: [],
};

QuizDropDownGroup.propTypes = {
  isEmbedded: PropTypes.bool,
  questionFieldsData: PropTypes.arrayOf(
    PropTypes.shape({
      options: PropTypes.array,
      label: PropTypes.string.isRequired,
      placeholder: PropTypes.string.isRequired,
      brands: PropTypes.objectOf(PropTypes.array),
    })
  ).isRequired,
  onSaveAnswers: PropTypes.func.isRequired,
  tabIndex: PropTypes.string,
  missingFields: PropTypes.array,
};

export default QuizDropDownGroup;
